'use client'

import { useId, type ElementType, type ReactNode } from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Logomark } from '@/components/brand'

export interface PageHeaderTab {
  id: string
  label: string
  count?: number
  href: string
}

export interface PageHeaderProps {
  title: string
  /** Japanese name printed before `title` in `lang="ja"` (司令室, plans/09 5.5). */
  titleJa?: string
  subtitle?: string
  /** `ampersand` shows the brand mark; a component shows that icon. */
  icon?: 'ampersand' | ElementType
  /** Shows the green "AI Employees online" pill on the right. */
  online?: boolean
  actions?: ReactNode
  tabs?: PageHeaderTab[]
  activeTab?: string
}

/** The header of every page (port of GolfTec `components/shell/PageHeader.tsx`; artifact `.ph`). */
export function PageHeader({
  title,
  titleJa,
  subtitle,
  icon = 'ampersand',
  online = false,
  actions,
  tabs,
  activeTab,
}: PageHeaderProps) {
  const Glyph = icon === 'ampersand' ? null : icon
  const titleId = useId()
  const fullTitle = titleJa ? `${titleJa} ${title}` : title
  return (
    <div className='mb-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-soft'>
            {Glyph ? (
              <Glyph className='h-5 w-5' strokeWidth={1.5} aria-hidden='true' />
            ) : (
              <Logomark variant='light' size={24} alt='' />
            )}
          </span>
          <div className='min-w-0'>
            <h1 id={titleId} title={fullTitle} className='truncate font-display text-2xl font-semibold tracking-tight text-foreground'>
              {titleJa && (
                <>
                  <span lang='ja'>{titleJa}</span>{' '}
                </>
              )}
              {title}
            </h1>
            {subtitle && <p className='text-sm text-muted-foreground'>{subtitle}</p>}
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          {online && (
            <span className='inline-flex items-center gap-1.5 rounded-full border border-success-strong/30 bg-success-strong/10 px-3 py-1 text-xs font-medium text-success-strong'>
              <span aria-hidden='true' className='h-1.5 w-1.5 rounded-full bg-success-strong' />
              AI Employees online
            </span>
          )}
          {actions}
        </div>
      </div>
      {tabs && tabs.length > 0 && (
        // Named by the h1 plus "sections" (aria-labelledby keeps the h1's lang="ja" for 司令室, WCAG 3.1.2).
        <nav aria-labelledby={`${titleId} ${titleId}-sections`} className='mt-4 flex gap-1 overflow-x-auto rounded-xl bg-tab-track p-1'>
          <span id={`${titleId}-sections`} hidden>
            sections
          </span>
          {tabs.map((tab) => {
            const active = tab.id === activeTab
            return (
              <Link
                key={tab.id}
                href={tab.href}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active ? 'bg-card text-foreground shadow-soft' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className='rounded-full bg-primary/10 px-1.5 text-xs text-foreground'>{tab.count}</span>
                )}
              </Link>
            )
          })}
        </nav>
      )}
    </div>
  )
}
